<script setup>
/**
 * All channels.
 *
 * Renders the full channel catalogue rather than only the inboxes that exist:
 * a connected channel shows its inbox count and a quiet Manage link, an
 * unconnected one a solid Set up button. The Intercom grammar leads with a
 * Recommended row (Live chat, Email, WhatsApp) and folds the rest under More
 * channels; both rows are filtered through the same per-account availability
 * gates as the Settings navigation, so the two surfaces never disagree about
 * which channels exist.
 */
import { computed, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  availableChannels,
  inboxesForChannel,
} from 'dashboard/helper/channelCatalogue';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsCardGrid from 'dashboard/components-next/Settings/SettingsCardGrid.vue';
import SettingsCard from 'dashboard/components-next/Settings/SettingsCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountScopedRoute, currentAccount } = useAccount();

const inboxes = useMapGetter('inboxes/getInboxes');

onActivated(() => {
  store.dispatch('inboxes/get');
});

const channels = computed(() =>
  availableChannels(currentAccount.value?.features).map(channel => ({
    ...channel,
    connected: inboxesForChannel(inboxes.value, channel.channelType),
  }))
);

const recommendedChannels = computed(() =>
  channels.value.filter(channel => channel.recommended)
);
const moreChannels = computed(() =>
  channels.value.filter(channel => !channel.recommended)
);

// Set up goes straight to the setup flow; Manage goes to the channel's page,
// which is what holds however many inboxes that channel has.
const openChannel = ({ key, connected }) => {
  if (!connected.length) {
    router.push(
      accountScopedRoute('settings_inboxes_page_channel', { sub_page: key })
    );
    return;
  }

  router.push(accountScopedRoute('settings_channel_show', { channel: key }));
};

const statusLabelFor = channel => {
  if (!channel.connected.length) return '';
  return channel.connected.length === 1
    ? t('CHANNELS.CARD.CONNECTED')
    : t('CHANNELS.CARD.CONNECTED_COUNT', { count: channel.connected.length });
};
</script>

<template>
  <SettingsPageShell wide :title="$t('SIDEBAR.ALL_CHANNELS')">
    <template v-if="isAdmin" #actions>
      <router-link :to="{ name: 'settings_inbox_new' }">
        <Button size="sm" :label="$t('CHANNELS.ACTION.PRIMARY')" />
      </router-link>
    </template>

    <SettingsSection flush :title="$t('CHANNELS.GROUPS.RECOMMENDED')">
      <SettingsCardGrid>
        <SettingsCard
          v-for="channel in recommendedChannels"
          :key="channel.key"
          :title="$t(channel.labelKey)"
          :description="$t(channel.descriptionKey)"
          :icon="channel.icon"
          :status-label="statusLabelFor(channel)"
          status-tone="success"
          :is-configured="Boolean(channel.connected.length)"
          :action-label="
            channel.connected.length
              ? $t('CHANNELS.CARD.MANAGE')
              : $t('CHANNELS.CARD.CONNECT')
          "
          @action="openChannel(channel)"
        />
      </SettingsCardGrid>
    </SettingsSection>

    <SettingsSection flush :title="$t('CHANNELS.GROUPS.MORE')">
      <SettingsCardGrid>
        <SettingsCard
          v-for="channel in moreChannels"
          :key="channel.key"
          :title="$t(channel.labelKey)"
          :description="$t(channel.descriptionKey)"
          :icon="channel.icon"
          :status-label="statusLabelFor(channel)"
          status-tone="success"
          :is-configured="Boolean(channel.connected.length)"
          :action-label="
            channel.connected.length
              ? $t('CHANNELS.CARD.MANAGE')
              : $t('CHANNELS.CARD.CONNECT')
          "
          @action="openChannel(channel)"
        />
      </SettingsCardGrid>
    </SettingsSection>
  </SettingsPageShell>
</template>
